import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Play, RotateCw, Trash2, Loader2, Wand2, Settings2, Bug } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ReportProblemDialog from "@/components/ReportProblemDialog";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch {
      toast.error("Could not load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [id]);

  const regenerate = async () => {
    try {
      await api.post(`/projects/${id}/generate`);
      toast.success("Regeneration started");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    }
  };

  const retryWithAlternate = async () => {
    try {
      await api.post(`/projects/${id}/retry`);
      toast.success("Trying another AI…");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch AI. Please try again.");
    }
  };

  const repairPhotos = async () => {
    try {
      const r = await api.post(`/projects/${id}/repair-photos`);
      const { repaired = 0, removed = 0, remaining = 0 } = r.data || {};
      if (removed) toast.warning(`Removed ${removed} unrepairable photo(s). ${remaining} left.`);
      else toast.success(`Repaired ${repaired} photo(s). Ready to retry.`);
      load();
    } catch {
      toast.error("Could not repair photos.");
    }
  };

  const del = async () => {
    if (!confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      nav("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!project) return null;

  const busy = project.status === "queued" || project.status === "processing";
  const done = project.status === "completed";
  const failed = project.status === "failed";

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]">
      <button
        onClick={() => nav("/")}
        data-testid="back-button"
        className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Dashboard
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-tech text-[10px] text-amber-400 mb-3">
            PROJECT · {project.settings?.style?.toUpperCase()} · {project.settings?.aspect} · {project.settings?.resolution?.toUpperCase()}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
            {project.name}
          </h1>
          {project.address && (
            <div className="text-zinc-400 mt-2">{project.address}</div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {done && project.video_filename && (
            <a
              href={fileUrl(`videos/${project.video_filename}`)}
              download
              data-testid="download-video"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm"
            >
              <Download className="w-4 h-4" strokeWidth={2} />
              Download
            </a>
          )}
          <Button
            variant="outline"
            onClick={regenerate}
            disabled={busy}
            data-testid="regenerate"
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            <RotateCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Regenerate
          </Button>
          <Button
            variant="outline"
            onClick={del}
            data-testid="delete-project"
            className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Delete
          </Button>
        </div>
      </div>

      {/* Player */}
      <div className={`bg-black rounded-lg border border-white/5 overflow-hidden mb-8 ${
        project.settings?.aspect === "9:16" ? "max-w-md mx-auto" :
        project.settings?.aspect === "1:1" ? "max-w-2xl mx-auto" : ""
      }`}>
        {done && project.video_filename ? (
          <div
            className={`w-full max-w-full mx-auto bg-black rounded-lg overflow-hidden ${
              project.settings?.aspect === "9:16"
                ? "aspect-[9/16] max-w-[420px]"
                : project.settings?.aspect === "1:1"
                  ? "aspect-square max-w-[560px]"
                  : "aspect-video"
            }`}
          >
            <video
              key={project.video_filename}
              controls
              preload="metadata"
              className="w-full h-full object-cover"
              poster={
                project.thumbnail_filename
                  ? fileUrl(`thumbnails/${project.thumbnail_filename}`)
                  : (project.photos?.[0]
                      ? fileUrl(`photos/${project.id}/${project.photos[0].filename}`)
                      : undefined)
              }
              data-testid="video-player"
            >
              <source src={fileUrl(`videos/${project.video_filename}`)} type="video/mp4" />
            </video>
          </div>
        ) : (
          <div className={`relative flex items-center justify-center ${
            project.settings?.aspect === "9:16" ? "aspect-[9/16]" :
            project.settings?.aspect === "1:1" ? "aspect-square" : "aspect-video"
          }`}>
            {project.photos?.[0] && (
              <img
                src={fileUrl(`photos/${project.id}/${project.photos[0].filename}`)}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
            )}
            <div className="relative z-10 text-center px-8">
              {busy ? (
                <>
                  <div className="font-mono-tech text-[10px] text-amber-400 mb-3">
                    {project.status.toUpperCase()} · {project.progress || 0}%
                  </div>
                  <div className="font-heading text-3xl font-bold tracking-tight mb-4">
                    Rendering your video…
                  </div>
                  <div className="text-zinc-400 text-sm mb-6">{project.progress_message}</div>
                  <div className="w-full max-w-md mx-auto h-1 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-amber-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${project.progress || 5}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </>
              ) : failed ? (
                <div className="max-w-lg mx-auto" data-testid="failed-error-card">
                  <div className="font-mono-tech text-[10px] text-red-400 mb-3">FAILED · PROJECT SAVED</div>
                  <div className="font-heading text-2xl font-bold tracking-tight mb-3">Video generation failed</div>
                  <div className="text-zinc-300 text-base leading-relaxed mb-2">
                    {project.error || "Something went wrong. Please try again."}
                  </div>
                  <div className="inline-flex items-center gap-2 text-emerald-400 text-sm mb-4 mt-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Your project has been saved — photos, prompt and settings are preserved.
                  </div>
                  {project.error_code && (
                    <div
                      className="inline-block font-mono-tech text-[11px] px-3 py-1.5 rounded bg-white/5 border border-white/10 text-zinc-400 mb-6"
                      data-testid="error-code"
                    >
                      Error code: <span className="text-zinc-200">{project.error_code}</span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 flex-wrap">
                    <Button
                      onClick={retryWithAlternate}
                      data-testid="try-another-ai"
                      className="bg-amber-500 hover:bg-amber-600 text-black font-bold w-full sm:w-auto"
                    >
                      <Wand2 className="w-4 h-4 mr-2" strokeWidth={2} />
                      Try another AI
                    </Button>
                    <Button
                      onClick={regenerate}
                      variant="outline"
                      data-testid="retry-same"
                      className="border-white/15 hover:bg-white/5 w-full sm:w-auto"
                    >
                      <RotateCw className="w-4 h-4 mr-2" strokeWidth={2} />
                      Retry
                    </Button>
                    {project.error_code === "MO-1003" && (
                      <Button
                        onClick={repairPhotos}
                        variant="outline"
                        data-testid="repair-photos"
                        className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 w-full sm:w-auto"
                      >
                        <Wand2 className="w-4 h-4 mr-2" strokeWidth={2} />
                        Fix photos
                      </Button>
                    )}
                    <Button
                      onClick={() => nav(`/projects/new?edit=${project.id}`)}
                      variant="ghost"
                      data-testid="edit-settings"
                      className="text-zinc-400 hover:text-white hover:bg-white/5 w-full sm:w-auto"
                    >
                      <Settings2 className="w-4 h-4 mr-2" strokeWidth={2} />
                      Edit settings
                    </Button>
                    <Button
                      onClick={() => setReportOpen(true)}
                      variant="ghost"
                      data-testid="report-problem"
                      className="text-zinc-400 hover:text-amber-400 hover:bg-white/5 w-full sm:w-auto"
                    >
                      <Bug className="w-4 h-4 mr-2" strokeWidth={2} />
                      Report problem
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Play className="w-12 h-12 text-amber-400 mx-auto mb-3" strokeWidth={1.5} />
                  <div className="font-heading text-2xl font-bold tracking-tight">Ready to render</div>
                  <div className="text-zinc-400 text-sm mt-2">Press <span className="text-amber-400">Regenerate</span> to start.</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Photos strip */}
      {project.photos?.length > 0 && (
        <div>
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">SOURCE · PHOTOS · {project.photos.length}</div>
          <div className="flex gap-3 overflow-x-auto pb-3">
            {project.photos.map((p) => (
              <div key={p.id} className="relative flex-shrink-0 w-32 rounded-lg overflow-hidden border border-white/5 bg-[#121214]">
                <img
                  src={fileUrl(`photos/${project.id}/${p.filename}`)}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement?.classList.add("photo-missing");
                  }}
                  className="w-full h-20 object-cover"
                />
                <div className="absolute inset-x-0 top-0 h-20 flex items-center justify-center bg-[#0b0b0d] pointer-events-none opacity-0 [.photo-missing_&]:opacity-100">
                  <div className="text-center px-2">
                    <div className="font-mono-tech text-[8px] text-zinc-500 uppercase">Photo · missing</div>
                  </div>
                </div>
                <div className="px-2 py-1.5 text-[10px] font-mono-tech uppercase text-zinc-400 truncate">
                  {p.scene?.replace("_", " ") || "auto"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ReportProblemDialog
        project={project}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
    </div>
  );
}
